import _ from 'lodash'
import React, { useState } from 'react'
import { useLanguage } from '@/hooks/useLanguage'
import { Link } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'
import { useNavigate } from 'react-router-dom'
import { t, Trans } from '@lingui/macro'

import { Theme, useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TableContainer from '@mui/material/TableContainer'

import PageContainer from '@/components/ui/containers/PageContainer'
import AddPatientIcon from '@/components/ui/icons/AddPatient'
import TextInput from '@/components/ui/inputs/TextInput'
import Pagination from '@/components/ui/pagination/Pagination'
import { PatientSourceMenu } from '@/components/ui/menu/PatientSourceMenu/PatientSourceMenu'

import { Patient } from '@/core/models/patient.model'
import { useSearchPatientsQuery } from '@/core/services/patient'

import { useCheckIfExists } from '@/hooks/useCheckIfExists'

import { useAppSelector } from '@/redux/rootReducer'
import { createPatient, selectNewlyCreatedPatientId, selectPatientCreateRequestTracker, selectPatients } from '@/redux/reducers/patient.reducer'

import { sanitizeSecu } from '@/utils/helpers'

import PatientsCardView from './PatientsCardView'
import PatientsTable from './PatientsTable'
import { PatientSourceEnum } from '@/core/enums/patient-source.enum'
import { useIsUS } from '@/hooks/useIsUS'
import { useIsSA } from '@/hooks/useIsSA'
import { AttachPatientDialog } from './AttachPatientDialog'
import { useDialogState } from '@/hooks/useDialogState'
import { DMPConnectVitaleCardDialog, useVitaleCardDisplayLogic } from '@/widgets/vitale-card'

const rowsPerPageOptions = [5, 10, 15]

const languagesWithSearchByVitale = ['fr']

export function PatientsPage() {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const language = useLanguage()
  const isUS = useIsUS()
  const isSA = useIsSA()

  const { dialogState, setDialogState, closeDialog } = useDialogState<'attach-patient' | 'search-by-vitale-card'>('none')
  const { showDMPConnectVitaleCard } = useVitaleCardDisplayLogic()

  const [itemsPerPage, setItemsPerPage] = useState<number>(rowsPerPageOptions[0])
  const [page, setPage] = useState<number>(1)
  const [searchText, setSearchText] = useState('')
  const [patientSource, setPatientSource] = useState(PatientSourceEnum.MedconnectPatient)
  const [searchPatientsArgs, setSearchPatientsArgs] = useState(
    // SEHA (en-SA) requirement: list the most recently created patients first.
    isSA
      ? {
          limit: itemsPerPage,
          skip: 0,
          orderBy: 'createdAt',
          order: 'DESC' as const,
          patientSource,
        }
      : {
          limit: itemsPerPage,
          skip: 0,
          orderBy: 'userInfo.surname',
          patientSource,
        }
  )

  const patients = useAppSelector(selectPatients)

  const {
    isFetching: isFetchingPatients,
    patientsData,
    totalPatients,
  } = useSearchPatientsQuery(searchPatientsArgs, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      patientsData:
        data?.items.map((patient) => ({
          _id: patient._id,
          name: patient.userInfo.name,
          surname: patient.userInfo.surname,
          gender: patient.userInfo.gender,
          birthday: patient.userInfo.birthday,
          city: patient.contactInfo?.city,
          secu: (isUS ? patient.primaryPolicy : patient.secu) ?? '',
        })) ?? [],
      totalPatients: data?.totalCount ?? 0,
    }),
  })

  const debouncedSearch = useDebouncedCallback((textSearch: string) => {
    setPage(1)
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, patientSource, skip: 0, smartSearch: textSearch }))
  }, 300)

  const handleSearchTextChange = (textSearch: string) => {
    setSearchText(textSearch)
    debouncedSearch(textSearch)
  }

  const handleSelectVitaleCardPatient = (searchTerm: string) => {
    setSearchText(searchTerm)
    setPage(1)
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, patientSource, skip: 0, smartSearch: searchTerm }))
    closeDialog()
  }

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, skip: (newPage - 1) * itemsPerPage }))
  }

  const handleChangeItemsPerPage = (newItemsPerPage: number) => {
    if (itemsPerPage === newItemsPerPage) return
    setPage(1)
    setItemsPerPage(newItemsPerPage)
    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, limit: newItemsPerPage, skip: 0 }))
  }

  const checkProps = {
    successMsg: t`notification.patient.create.success`,
    errorMsg: t`notification.patient.create.error`,
    itemTracker: selectPatientCreateRequestTracker,
    createAction: (patient: Patient) =>
      createPatient({
        netsoinsId: patient.netsoinsId,
        pluginId: patient.netsoinsId,
        userInfo: patient.userInfo,
        secu: sanitizeSecu(patient.secu),
        agreement: `from plugin original id: ${patient._id}`,
      }),
    latestIDSelector: useAppSelector(selectNewlyCreatedPatientId),
    itemList: patients,
  }

  const checkLocal = useCheckIfExists<Patient>({
    ...checkProps,
    callback: (patient: Patient) => {
      if (!patient) return
      navigate(`/patients/${patient._id}`)
    },
  })

  return (
    <PageContainer>
      <Grid container spacing={2}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button component={Link} disableElevation startIcon={<AddPatientIcon htmlColor="#FFF" />} to="/patients/create" variant="contained">
                <Trans>New patient</Trans>
              </Button>
              <Button component={Link} to="/patients/merge" variant="outlined">
                <Trans>Merge patients</Trans>
              </Button>
              <Button disableElevation variant="outlined" onClick={() => setDialogState('attach-patient')}>
                <Trans>Attach Patient</Trans>
              </Button>
              {!isMobile && languagesWithSearchByVitale.includes(language) && showDMPConnectVitaleCard && (
                <Button onClick={() => setDialogState('search-by-vitale-card')} variant="outlined">
                  <Trans>Search by vitale card</Trans>
                </Button>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} lg={8} xl={9}>
            <TextInput
              fullWidth
              value={searchText}
              onChange={(event) => handleSearchTextChange(event.target.value)}
              placeholder={t`Search patients (by name, surname, social security number)`}
            />
          </Grid>
          <Grid item xs={12} lg={4} xl={3}>
            <Stack direction="row" justifyContent="end">
              {languagesWithSearchByVitale.includes(language) && (
                <PatientSourceMenu
                  onChange={(patientSource) => {
                    setPage(1)
                    setPatientSource(patientSource)
                    setSearchPatientsArgs((prevArgs) => ({ ...prevArgs, patientSource, skip: 0 }))
                  }}
                  value={patientSource}
                />
              )}
            </Stack>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {isMobile ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
                <Pagination
                  color="primary"
                  itemsPerPageOptions={rowsPerPageOptions}
                  shape="rounded"
                  page={page}
                  onPageChange={handleChangePage}
                  onItemsPerPageChange={handleChangeItemsPerPage}
                  totalItems={totalPatients}
                />
              </Box>
              <PatientsCardView disableSelectRow data={patientsData} loading={isFetchingPatients} />
            </Box>
          ) : (
            <Grid container item xs={12} spacing={4}>
              <Grid item xs={12}>
                <Paper>
                  <TableContainer>
                    <PatientsTable
                      disableSelectRow
                      data={patientsData}
                      loading={isFetchingPatients}
                      onOpen={(patient, { disableSelectRow }) => {
                        if (disableSelectRow) checkLocal(patient._id)
                        else {
                          navigate(`/patients/${patient._id}`)
                        }
                      }}
                      showAddToPatientQueueButton
                    />
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
                    <Pagination
                      color="primary"
                      itemsPerPageOptions={rowsPerPageOptions}
                      shape="rounded"
                      page={page}
                      onPageChange={handleChangePage}
                      onItemsPerPageChange={handleChangeItemsPerPage}
                      totalItems={totalPatients}
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <AttachPatientDialog open={dialogState === 'attach-patient'} onClose={closeDialog} />
      <DMPConnectVitaleCardDialog
        hideGetIns
        open={dialogState === 'search-by-vitale-card'}
        onClose={closeDialog}
        title={t`Search by vitale card`}
        onSelectPatient={(patient) => handleSelectVitaleCardPatient(`${patient.s_name} ${patient.s_given}`.trim())}
      />
    </PageContainer>
  )
}

export default PatientsPage
